import React, { useState } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  FooterWrapper,
  FooterPlaceholder,
  FooterContainer,
  FooterDisclaimer,
  FooterContent,
  TotalFee,
  FooterButtons,
} from "./TreatmentPlanFooter.styled";
import { Popover, SharePopoverContent } from "../../../../components/Popover";
import { colors } from "../../../../theme/colors";

interface TreatmentPlanFooterProps {
  totalFee: string | number | null | undefined;
  status: number;
  isProvider: boolean;
  hasMultipleOptions?: boolean;
  onApprove: () => void;
  onDownloadPdf: () => void;
  onEmailForReview: () => void;
  onOpenToSign: () => void;
}

/**
 * Treatment plan footer with total fee and action buttons
 *
 * Status logic (according to requirements):
 * Scenario 1 - status 1, 2 (AI Drafted, Awaiting Provider Review):
 *   - "Approve" button active
 *   - "Share with Patient" button disabled
 *
 * Scenario 2 - status 4, 5, 6, 7 (Declined, Approved, InProgress, Completed):
 *   - "Download as PDF" button only
 *
 * Scenario 3 - status 3 (Awaiting Patient Confirmation):
 *   - "Share with Patient" button active with popover (PDF, Email, Sign)
 */
const TreatmentPlanFooter: React.FC<TreatmentPlanFooterProps> = ({
  totalFee,
  status,
  isProvider,
  hasMultipleOptions = false,
  onApprove,
  onDownloadPdf,
  onEmailForReview,
  onOpenToSign,
}) => {
  const { t } = useTranslation();
  const [shareAnchorEl, setShareAnchorEl] = useState<HTMLElement | null>(null);
  const isSharePopoverOpen = Boolean(shareAnchorEl);

  // Scenario 1: Awaiting Provider Review (status 1 or 2)
  const isAwaitingProviderReview = status === 1 || status === 2;
  // Scenario 2: After provider approval - shows only Download PDF (status 4, 5, 6, 7)
  const isAfterApproval =
    status === 4 || status === 5 || status === 6 || status === 7;
  // Scenario 3: Awaiting Patient Confirmation (status 3)
  const isAwaitingPatientConfirmation = status === 3;

  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const handleDownloadPdf = () => {
    handleShareClose();
    onDownloadPdf();
  };

  const handleEmailForReview = () => {
    handleShareClose();
    onEmailForReview();
  };

  const handleOpenToSign = () => {
    handleShareClose();
    onOpenToSign();
  };

  // Format fee for display
  const formatFee = () => {
    if (totalFee === null || totalFee === undefined) return "$0";
    if (typeof totalFee === "number")
      return `$${totalFee.toLocaleString("en-US")}`;
    if (typeof totalFee === "string") {
      return totalFee.startsWith("$") ? totalFee : `$${totalFee}`;
    }
    return "$0";
  };
  const formattedFee = formatFee();

  // Render buttons based on scenario
  const renderButtons = () => {
    if (!isProvider) return null;

    // Scenario 1: Awaiting Provider Review - Show disabled Share + Approve
    if (isAwaitingProviderReview) {
      return (
        <FooterButtons>
          <Button
            variant="contained"
            disabled
            sx={{
              backgroundColor: colors.amber.main,
              "&:hover": {
                backgroundColor: colors.amber.dark,
              },
            }}
          >
            {t("treatmentPlan.shareWithPatient")}
          </Button>
          <Button
            variant="contained"
            onClick={onApprove}
            sx={{
              minWidth: "112px!important",
              background: colors.status.success,
            }}
          >
            {t("treatmentPlan.approve")}
          </Button>
        </FooterButtons>
      );
    }

    // Scenario 2: After approval (Declined, Approved, InProgress, Completed) - Show only Download PDF
    if (isAfterApproval) {
      return (
        <FooterButtons>
          <Button
            variant="contained"
            onClick={onDownloadPdf}
            sx={{
              backgroundColor: colors.primary.main,
              "&:hover": {
                backgroundColor: colors.primary.dark,
              },
            }}
          >
            {t("treatmentPlan.downloadAsPdf")}
          </Button>
        </FooterButtons>
      );
    }

    // Scenario 3: Awaiting Patient Confirmation - Show Share with options
    if (isAwaitingPatientConfirmation) {
      return (
        <FooterButtons>
          <Button
            variant="contained"
            onClick={handleShareClick}
            sx={{
              backgroundColor: colors.amber.main,
              "&:hover": {
                backgroundColor: colors.amber.dark,
              },
            }}
          >
            {t("treatmentPlan.shareWithPatient")}
          </Button>
          <Popover
            open={isSharePopoverOpen}
            anchorEl={shareAnchorEl}
            onClose={handleShareClose}
          >
            <SharePopoverContent
              onDownloadPdf={handleDownloadPdf}
              onEmailForReview={handleEmailForReview}
              onOpenToSign={handleOpenToSign}
              status={status}
            />
          </Popover>
        </FooterButtons>
      );
    }

    return null;
  };

  // Display fee - show $0,00 when multiple options, otherwise show actual fee
  const displayFee = hasMultipleOptions ? "$0,00" : formattedFee;

  return (
    <FooterWrapper>
      {hasMultipleOptions && (
        <FooterPlaceholder>
          {t("treatmentPlan.totalFeePlaceholder")}
        </FooterPlaceholder>
      )}
      <FooterContainer>
        <FooterDisclaimer>
          {t("treatmentPlan.timeEstimateDisclaimer")}
          <br />
          {t("treatmentPlan.feesDisclaimer")}
        </FooterDisclaimer>
        <FooterContent>
          <TotalFee>
            {t("treatmentPlan.totalFee")} {displayFee}
          </TotalFee>
          {renderButtons()}
        </FooterContent>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default TreatmentPlanFooter;
